import { useMutation, useQueryClient } from '@tanstack/react-query';

import { QUERY_KEYS } from '@/constants/query-keys';
import { sessionTokenStore } from '@/services/session/token-store';

import { changePassword, login, logout, updateProfile } from '../api/auth.api';
import type {
  ChangePasswordInput,
  LoginFormValues,
  UpdateProfileInput,
} from '../schemas/auth.schema';

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ identifier, password }: LoginFormValues) => login(identifier, password),
    onSuccess: (result, { remember }) => {
      // Profile first, token second: the guards then have everything on their first render.
      queryClient.setQueryData(QUERY_KEYS.auth.me(), result.user);
      sessionTokenStore.set(result.token, remember);
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      try {
        await logout();
      } catch {
        // Signing out locally must work even when the server can't be reached.
      }
    },
    onSettled: () => {
      sessionTokenStore.clear();
      queryClient.clear();
    },
  });
}

/** Saves the caller's own name. The profile in the cache is replaced, so every screen shows it. */
export function useUpdateProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateProfileInput) => updateProfile(input),
    onSuccess: (profile) => {
      queryClient.setQueryData(QUERY_KEYS.auth.me(), profile);
    },
  });
}

export function useChangePassword() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ChangePasswordInput) => changePassword(input),
    onSuccess: (profile) => {
      queryClient.setQueryData(QUERY_KEYS.auth.me(), profile);
    },
  });
}
